import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useGetContainerDetails, useGetContainers } from '@/hooks/api/dashboard'
import SelectTopics from './SelectTopics'
import { shallow } from 'zustand/shallow'

import { ScrollableCapsulesTabBar } from '@/components/ScrollableCapsulesTabBar'
import { SIDE_PANEL_WIDTH } from '@/utils/constants'
import dayjs from 'dayjs'
import UploadPhotos from './Photos'
import colors from '@/styles/colors'
import { Container, EgoObject } from '@/types'
import { TransitionProps } from '@mui/material/transitions'
import { ArrowBackIos } from '@mui/icons-material'
import ContainersGrid from './molecules/ContainersGrid'
import { ContainerListContext } from '@/context/ContainersListContext'
import { ContentPanel, RBox } from '../__styled'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { RModal } from '@/components'
import toast from 'react-hot-toast'
import { useMediaUploadsViewModel } from '@/hooks/api/dashboard/viewModels/useMediaUploadsViewModel'
import { pluralize } from '@/utils/helper'

// Stripe-level Styled Components
const UploadDestinationsContainer = styled(Box)({
  //backgroundColor: '#f8fafc',
  padding: '32px',
  minHeight: '100vh',
  overflow: 'visible',
})

const SectionTitle = styled(Typography)({
  fontSize: '32px',
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.02em',
  marginBottom: '8px',
  fontFamily: 'Inter, sans-serif',
  lineHeight: 1.2,
})

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />
})

export default function UploadsPage() {
  const [selectedEgoList, setSelectedEgoList] = useState<EgoObject[]>([])
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const getContainerDetails = useGetContainerDetails()
  const {
    loading,
    loadingNextPage,
    canLoadNextPage,
    containers,
    getContainersList,
    loadNextPageData,
    deleteEgoObjects,
    pagination,
  } = useMediaUploadsViewModel()
  const theme = useTheme()
  const lessThanMedium = useMediaQuery(theme.breakpoints.down('md'))
  const sidePanelWidth = lessThanMedium ? 0 : '420px'
  const [selectedContainerID, setSelectedContainerID] = useState<string | null>(null)
  // const [selectedContainer, setSelectedContainer] = useState<Container | null>(null)
  const [currentContainerEgoObjects, setCurrentContainerEgoObjects] = useState<EgoObject[] | null>(
    null,
  )
  const isDeleting = useRef(false)

  useEffect(() => {
    getContainersList({ pageNo: 1 })
  }, [])

  useEffect(() => {
    if (selectedContainerID) {
      getContainerDetails.mutate(selectedContainerID)
    }
  }, [selectedContainerID])

  useEffect(() => {
    if (getContainerDetails.isSuccess) {
      setCurrentContainerEgoObjects(getContainerDetails.data?.egoObjects || [])
    }
  }, [getContainerDetails.isSuccess])

  const selectedContainer = useMemo(() => {
    if (containers.length > 0) return containers.find((e) => e.containerID == selectedContainerID)
  }, [selectedContainerID, containers])

  const onClickBack = () => {
    setSelectedContainerID(null)
  }

  const currentContainerIndex = useMemo(() => {
    if (selectedContainerID && containers.length > 0) {
      return containers.findIndex((e) => e.containerID == selectedContainerID)
    }
    return null
  }, [containers, selectedContainerID])

  const onClickNext = () => {
    if (currentContainerIndex !== null) {
      if (currentContainerIndex + 1 < containers.length) {
        setSelectedContainerID(containers[currentContainerIndex + 1].containerID)
      }
    }
  }

  const onClickPrev = () => {
    if (currentContainerIndex !== null) {
      if (currentContainerIndex - 1 >= 0) {
        setSelectedContainerID(containers[currentContainerIndex - 1].containerID)
      }
    }
  }

  const onClickCloseInteractions = () => {
    setSelectedEgoList([])
  }

  const onClickShowDrawer = () => {
    setShowDetailsDrawer(true)
  }
  const onClickHideDrawer = () => {
    setShowDetailsDrawer(false)
  }

  const onClickDeletePhotos = () => {
    if (selectedContainer && selectedEgoList.length > 0 && !isDeleting.current) {
      setOpenDeleteModal(true)
    }
  }
  const deletePhotosOnServer = async () => {
    if (selectedContainer && selectedEgoList.length > 0) {
      toast.loading('Deleting photos', { id: 'delete' })
      setSelectedEgoList([])
      isDeleting.current = true
      const egoIDs = selectedEgoList.map((e) => e.egoID)
      let filtered = (currentContainerEgoObjects || []).filter((e) => !egoIDs.includes(e.egoID))
      setCurrentContainerEgoObjects(filtered)
      await deleteEgoObjects(selectedContainer.containerID, egoIDs)
      isDeleting.current = false
      toast.remove('delete')
      toast.success(`${pluralize('', egoIDs.length, 'Photo', 'Photos')} deleted!`, {
        duration: 2000,
      })
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Box
        flex={1}
        display={'flex'}
        flexDirection={'column'}
        sx={{
          overflow: 'auto',
          '& > *': {
            overflow: 'visible',
          },
        }}
      >
        {!selectedContainer && (
          <UploadDestinationsContainer>
            <Box display='flex' alignItems='center' justifyContent='space-between' mb={4}>
              <Box>
                <SectionTitle>Uploads</SectionTitle>
                <Typography
                  variant='body1'
                  sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#64748b',
                    fontFamily: 'Inter, sans-serif',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {loading ? (
                    'Loading your folders...'
                  ) : (
                    <>
                      <Box component='span' sx={{ color: '#0f172a', fontWeight: 700 }}>
                        {containers?.length || 0}
                      </Box>{' '}
                      upload folder{containers?.length === 1 ? '' : 's'}
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            <ContainerListContext.Provider
              value={{
                items: containers,
                loading: loading,
                loadingNextPage,
                canLoadNextPage,
                loadNextPageData,
                total: pagination?.containerCount || 0, // TODO: use pagination to pass total
              }}
            >
              <ContainersGrid onClick={(item) => setSelectedContainerID(item.containerID)} />
            </ContainerListContext.Provider>
          </UploadDestinationsContainer>
        )}

        {selectedContainer && (
          <Box flexDirection={'row'} display={'flex'} flex={1} sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                gap: '16px',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px 20px',
                width:
                  !lessThanMedium && selectedEgoList.length > 0
                    ? `calc(100% - ${sidePanelWidth})`
                    : '100%',
              }}
            >
              {/* Stripe-level Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '24px 32px',
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Box display='flex' alignItems='center' gap={2.5}>
                  <IconButton
                    onClick={onClickBack}
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: 'rgba(248, 250, 252, 0.8)',
                      border: '1px solid rgba(226, 232, 240, 0.6)',
                      borderRadius: '12px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: '#ffffff',
                        transform: 'translateX(-2px)',
                        borderColor: '#6366f1',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                      },
                      '&:active': {
                        transform: 'translateX(0)',
                      },
                    }}
                  >
                    <SVG src={SVGS.BackArrow} color='#475569' width='18' height='18' />
                  </IconButton>

                  <Box>
                    <Typography
                      variant='h5'
                      sx={{
                        fontSize: '22px',
                        fontWeight: 700,
                        color: '#0f172a',
                        letterSpacing: '-0.02em',
                        marginBottom: '4px',
                        lineHeight: 1.2,
                      }}
                    >
                      {selectedContainer
                        ? dayjs(selectedContainer?.datePurchased).format('MMMM YYYY')
                        : ''}
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                        border: '1px solid rgba(99, 102, 241, 0.15)',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontSize: '13px',
                          color: '#6366f1',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {selectedContainer?.photoCount || 0} Photos
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box display='flex' alignItems='center' gap={1.5}>
                  <IconButton
                    onClick={onClickPrev}
                    disabled={currentContainerIndex === 0}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      background:
                        currentContainerIndex === 0
                          ? 'rgba(248, 250, 252, 0.8)'
                          : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      border: '1px solid',
                      borderColor:
                        currentContainerIndex === 0 ? 'rgba(226, 232, 240, 0.6)' : 'transparent',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background:
                          currentContainerIndex === 0
                            ? 'rgba(248, 250, 252, 0.8)'
                            : 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)',
                        transform: currentContainerIndex === 0 ? 'none' : 'translateY(-2px)',
                        boxShadow:
                          currentContainerIndex === 0
                            ? 'none'
                            : '0 4px 12px rgba(99, 102, 241, 0.25)',
                      },
                      '&:active': {
                        transform: currentContainerIndex === 0 ? 'none' : 'translateY(0)',
                      },
                      '&:disabled': {
                        background: 'rgba(248, 250, 252, 0.8)',
                        color: '#cbd5e1',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    <SVG src={SVGS.BackArrow} width='16' height='16' />
                  </IconButton>

                  <IconButton
                    onClick={onClickNext}
                    disabled={currentContainerIndex === containers.length - 1}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      background:
                        currentContainerIndex === containers.length - 1
                          ? 'rgba(248, 250, 252, 0.8)'
                          : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      border: '1px solid',
                      borderColor:
                        currentContainerIndex === containers.length - 1
                          ? 'rgba(226, 232, 240, 0.6)'
                          : 'transparent',
                      transform: 'scaleX(-1)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background:
                          currentContainerIndex === containers.length - 1
                            ? 'rgba(248, 250, 252, 0.8)'
                            : 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)',
                        transform:
                          currentContainerIndex === containers.length - 1
                            ? 'scaleX(-1)'
                            : 'scaleX(-1) translateY(-2px)',
                        boxShadow:
                          currentContainerIndex === containers.length - 1
                            ? 'none'
                            : '0 4px 12px rgba(99, 102, 241, 0.25)',
                      },
                      '&:active': {
                        transform:
                          currentContainerIndex === containers.length - 1
                            ? 'scaleX(-1)'
                            : 'scaleX(-1) translateY(0)',
                      },
                      '&:disabled': {
                        background: 'rgba(248, 250, 252, 0.8)',
                        color: '#cbd5e1',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    <SVG src={SVGS.BackArrow} width='16' height='16' />
                  </IconButton>
                </Box>
              </Box>
              {lessThanMedium && selectedEgoList.length > 0 ? (
                <RBox justifyContent={'flex-end'}>
                  <Button variant='text' onClick={() => setShowDetailsDrawer(true)}>
                    {'Select Collections'}
                  </Button>
                </RBox>
              ) : (
                <Box width={'1px'} height={'2px'} />
              )}

              <UploadPhotos
                onClickShowDrawer={onClickShowDrawer}
                selectedEgoList={selectedEgoList}
                onSelectEgos={setSelectedEgoList}
                container={selectedContainer}
                isLoading={getContainerDetails.isLoading}
                egoObjects={currentContainerEgoObjects || []}
              />
            </Box>

            {!lessThanMedium && selectedEgoList.length > 0 && (
              <ContentPanel
                sx={{
                  height: '100%',
                  width: sidePanelWidth,
                  background: colors.gradients.interactionPanel,
                }}
              >
                <SelectTopics
                  selectedEgoObjects={selectedEgoList}
                  onClickBack={onClickCloseInteractions}
                  onClickDelete={onClickDeletePhotos}
                  showClose
                />
              </ContentPanel>
            )}
          </Box>
        )}
      </Box>
      <Dialog
        open={showDetailsDrawer && lessThanMedium}
        TransitionComponent={Transition}
        onClose={onClickHideDrawer}
        aria-describedby='alert-dialog-slide-description'
        fullWidth
        fullScreen
      >
        <SelectTopics
          selectedEgoObjects={selectedEgoList}
          onClickBack={() => {
            onClickCloseInteractions()
            onClickHideDrawer()
          }}
          showClose={true}
          onClickDelete={onClickDeletePhotos}
        />
      </Dialog>
      <RModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title={`Delete ${selectedEgoList.length} photos`}
        description='Are you sure you want to photos?'
        submitText='Delete'
        onSubmit={deletePhotosOnServer}
      />
    </Box>
  )
}
